import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { mediaUrl } from '@/lib/mediaUrl';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Award, BadgeCheck, HeartHandshake, ShieldCheck, Star, Upload } from 'lucide-react';

const ICONS = [HeartHandshake, Award, ShieldCheck, Star, BadgeCheck];

const BadgeMark = ({ b, i }) => {
  const Icon = ICONS[i % ICONS.length];
  const isTrustpilot = /trustpilot/i.test(b.label);
  return (
    <div className="flex items-center gap-3 min-w-0" data-testid={`trust-badge-${i}`}>
      {b.logo_url ? <img src={mediaUrl(b.logo_url)} alt={b.label} className="h-10 w-auto max-w-[120px] object-contain" /> : (
        <span className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${isTrustpilot ? 'bg-[#00b67a]' : 'bg-[#2a3e5d]'}`}>
          <Icon className={`h-5 w-5 ${isTrustpilot ? 'text-white fill-white' : 'text-[#02d1d4]'}`} />
        </span>
      )}
      <div className="min-w-0">
        <p className="font-bold text-[#2a3e5d] text-sm leading-tight">{b.label}</p>
        {isTrustpilot ? (
          <p className="flex items-center gap-1 mt-0.5">
            {[0, 1, 2, 3, 4].map((s) => <span key={s} className="h-3.5 w-3.5 bg-[#00b67a] flex items-center justify-center"><Star className="h-2.5 w-2.5 text-white fill-white" /></span>)}
            <span className="text-xs text-slate-500 ml-1">{b.sublabel}</span>
          </p>
        ) : <p className="text-xs text-slate-500 mt-0.5">{b.sublabel}</p>}
      </div>
    </div>
  );
};

export const TrustBanner = () => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/hub/trust-banner`).then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="h-[168px] bg-white border-y border-slate-200/70" />;
  return (
    <section className="bg-white border-y border-slate-200/70" data-testid="trust-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <p className="text-center text-sm md:text-base font-semibold text-[#2a3e5d] tracking-tight" data-testid="trust-banner-heading">{data.heading}</p>
        <div className="mt-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-6 items-center">
          {data.badges.map((b, i) => (b.link ? <a key={i} href={b.link} target="_blank" rel="noopener noreferrer"><BadgeMark b={b} i={i} /></a> : <BadgeMark key={i} b={b} i={i} />))}
        </div>
      </div>
    </section>
  );
};

export const TrustBannerEditor = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/hub/trust-banner`).then((r) => setData(r.data)); }, []);
  if (!data) return null;
  const setBadge = (i, k, v) => setData((d) => ({ ...d, badges: d.badges.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const upload = async (i, file) => {
    const fd = new FormData(); fd.append('file', file); fd.append('alt_text', data.badges[i].label);
    try { const r = await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setBadge(i, 'logo_url', r.data.url); toast.success('Logo uploaded — remember to save'); }
    catch { toast.error('Upload failed'); }
  };
  const save = async () => {
    try { await api.put('/hub/admin/trust-banner', data); toast.success('Trust banner saved'); } catch { toast.error('Could not save'); }
  };
  return (
    <Card className="border-slate-200 mt-6" data-testid="trust-banner-editor">
      <CardHeader className="flex flex-row items-center justify-between space-y-0"><CardTitle className="text-lg">Trust & accreditation banner</CardTitle>
        <Button size="sm" onClick={save} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="trust-banner-save">Save banner</Button></CardHeader>
      <CardContent className="space-y-3">
        <Input value={data.heading} onChange={(e) => setData({ ...data, heading: e.target.value })} data-testid="trust-banner-heading-input" />
        {data.badges.map((b, i) => (
          <div key={i} className="grid md:grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center">
            <Input value={b.label} onChange={(e) => setBadge(i, 'label', e.target.value)} placeholder="Name" data-testid={`trust-badge-label-${i}`} />
            <Input value={b.sublabel || ''} onChange={(e) => setBadge(i, 'sublabel', e.target.value)} placeholder="Small text" />
            <Input value={b.link || ''} onChange={(e) => setBadge(i, 'link', e.target.value || null)} placeholder="Link (optional)" />
            <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm cursor-pointer hover:bg-slate-50 whitespace-nowrap">
              <Upload className="h-4 w-4" />{b.logo_url ? 'Replace logo' : 'Upload logo'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(i, e.target.files[0])} data-testid={`trust-badge-upload-${i}`} />
            </label>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
